import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  DataState,
  Input,
  Modal,
  PageHeader,
  Select,
  Table,
  useToast,
  type Column,
} from "@vms/ui-web";
import { useApi, useCanIDo } from "../auth/AuthProvider";
import { useOrgContext } from "../context/OrgContext";
import type { VenueListItem } from "../api/types";

export function Venues() {
  const { currentOrg, venues, venuesLoading, refetchVenues } = useOrgContext();
  const canIDo = useCanIDo();
  const [creating, setCreating] = useState(false);
  const [revealKey, setRevealKey] = useState<{ venueName: string; venueKey: string } | null>(null);

  if (!currentOrg) return null;
  const canCreate = canIDo("venues:create");

  const columns: Column<VenueListItem>[] = [
    {
      key: "name",
      header: "Name",
      render: (venue) => (
        <Link
          to={`/org/${currentOrg.slug}/venue/${venue.slug}`}
          className="font-medium text-gray-900 hover:underline"
        >
          {venue.name}
        </Link>
      ),
    },
    {
      key: "slug",
      header: "Slug",
      render: (venue) => <code className="text-xs text-gray-500">{venue.slug}</code>,
    },
    {
      key: "address",
      header: "Address",
      render: (venue) => (
        <span className="text-sm text-gray-600 truncate max-w-xs inline-block">
          {venue.address || "—"}
        </span>
      ),
    },
    {
      key: "licencePlan",
      header: "Plan",
      render: (venue) => (
        <Badge className="bg-gray-100 text-gray-800">{venue.licencePlan ?? "—"}</Badge>
      ),
    },
    {
      key: "channel",
      header: "Channel",
      render: (venue) => (
        <Badge
          className={
            venue.channel === "dev"
              ? "bg-purple-100 text-purple-700"
              : venue.channel === "beta"
                ? "bg-amber-100 text-amber-700"
                : "bg-green-100 text-green-700"
          }
        >
          {venue.channel ?? "stable"}
        </Badge>
      ),
    },
    {
      key: "createdAt",
      header: "Created",
      render: (venue) => (
        <span className="text-sm text-gray-500">{new Date(venue.createdAt).toLocaleDateString()}</span>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Venues"
        subtitle={`Venues belonging to ${currentOrg.name}.`}
      >
        {canCreate ? (
          <Button onClick={() => setCreating(true)}>New venue</Button>
        ) : (
          <span className="text-xs text-gray-500">Ask FPZERO to mint a new venueKey.</span>
        )}
      </PageHeader>
      <Card className="overflow-hidden">
        <DataState
          loading={venuesLoading}
          empty={!venuesLoading && venues.length === 0}
          emptyMessage="No venues in this organisation yet."
        >
          <Table<VenueListItem>
            columns={columns}
            data={venues}
            keyExtractor={(v) => v.id}
          />
        </DataState>
      </Card>

      <NewVenueModal
        open={creating}
        onClose={() => setCreating(false)}
        organisationId={currentOrg.id}
        onCreated={(venueName, venueKey) => {
          refetchVenues();
          setRevealKey({ venueName, venueKey });
        }}
      />

      <Modal
        isOpen={!!revealKey}
        onClose={() => setRevealKey(null)}
        title="venueKey — copy now"
      >
        {revealKey && (
          <div className="space-y-4">
            <div className="text-sm text-gray-600">
              This is the only time you'll see <span className="font-medium">{revealKey.venueName}</span>'s
              venueKey. The cloud only retains the bcrypt hash — paste this into the hub's
              setup wizard before closing.
            </div>
            <pre className="rounded bg-gray-50 border border-gray-200 p-3 text-xs overflow-x-auto select-all">
              {revealKey.venueKey}
            </pre>
            <div className="flex justify-end">
              <Button onClick={() => setRevealKey(null)}>Done</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function NewVenueModal({
  open,
  onClose,
  organisationId,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  organisationId: string;
  onCreated: (venueName: string, venueKey: string) => void;
}) {
  const api = useApi();
  const { addToast } = useToast();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [address, setAddress] = useState("");
  const [licencePlan, setLicencePlan] = useState("standard");
  const [channel, setChannel] = useState<"stable" | "beta" | "dev">("stable");
  const [termStartsAt, setTermStartsAt] = useState(todayIso());
  const [termDurationMonths, setTermDurationMonths] = useState(12);
  const [termEndsAt, setTermEndsAt] = useState(addMonthsIso(todayIso(), 12));
  const [termEndsTouched, setTermEndsTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setName("");
    setSlug("");
    setSlugTouched(false);
    setAddress("");
    setLicencePlan("standard");
    setChannel("stable");
    const t = todayIso();
    setTermStartsAt(t);
    setTermDurationMonths(12);
    setTermEndsAt(addMonthsIso(t, 12));
    setTermEndsTouched(false);
    setError(null);
  }

  function slugify(s: string): string {
    return s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  function todayIso(): string {
    return new Date().toISOString().slice(0, 10);
  }

  function addMonthsIso(iso: string, months: number): string {
    const d = new Date(iso);
    d.setUTCMonth(d.getUTCMonth() + months);
    return d.toISOString().slice(0, 10);
  }

  async function submit() {
    if (!name.trim() || !slug.trim()) {
      setError("Name and slug are required");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await api("/api/venues", {
        method: "POST",
        body: JSON.stringify({
          organisationId,
          name: name.trim(),
          slug: slug.trim(),
          address: address.trim() || undefined,
          licencePlan,
          channel,
          initialTerm: {
            startsAt: new Date(termStartsAt).toISOString(),
            durationMonths: termDurationMonths,
            endsAt: termEndsTouched ? new Date(termEndsAt).toISOString() : undefined,
          },
        }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        setError(json.error ?? "Failed to create venue");
        return;
      }
      addToast(`Created ${name}`);
      onCreated(json.data.venue.name, json.data.venueKey);
      reset();
      onClose();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      isOpen={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title="New venue"
    >
      <div className="space-y-4">
        <Input
          label="Name"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (!slugTouched) setSlug(slugify(e.target.value));
          }}
          placeholder="Richmond Park"
          autoFocus
        />
        <Input
          label="Slug"
          value={slug}
          onChange={(e) => {
            setSlugTouched(true);
            setSlug(e.target.value);
          }}
          placeholder="richmond-park"
        />
        <Input
          label="Address"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder="1 Park Road, London"
        />
        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Licence plan"
            value={licencePlan}
            onChange={(e) => setLicencePlan(e.target.value)}
            options={[
              { value: "standard", label: "Standard" },
              { value: "premium", label: "Premium" },
              { value: "enterprise", label: "Enterprise" },
            ]}
          />
          <Select
            label="Update channel"
            value={channel}
            onChange={(e) => setChannel(e.target.value as "stable" | "beta" | "dev")}
            options={[
              { value: "stable", label: "Stable" },
              { value: "beta", label: "Beta" },
              { value: "dev", label: "Dev" },
            ]}
          />
        </div>
        <div className="rounded-md border border-gray-200 bg-gray-50/40 p-3 space-y-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-600">
            Licensing term
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Input
              label="Term starts"
              type="date"
              value={termStartsAt}
              onChange={(e) => {
                const next = e.target.value;
                setTermStartsAt(next);
                if (!termEndsTouched) {
                  setTermEndsAt(addMonthsIso(next, termDurationMonths));
                }
              }}
            />
            <Input
              label="Duration (months)"
              type="number"
              value={String(termDurationMonths)}
              onChange={(e) => {
                const n = Math.max(1, Math.min(120, Number(e.target.value) || 12));
                setTermDurationMonths(n);
                if (!termEndsTouched) setTermEndsAt(addMonthsIso(termStartsAt, n));
              }}
            />
            <Input
              label="Term ends"
              type="date"
              value={termEndsAt}
              onChange={(e) => {
                setTermEndsTouched(true);
                setTermEndsAt(e.target.value);
              }}
            />
          </div>
          <div className="text-xs text-gray-500">
            Cert refreshes will be denied after {new Date(termEndsAt).toLocaleDateString()} unless the term is renewed.
          </div>
        </div>
        {error && (
          <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button
            variant="secondary"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button onClick={submit} loading={submitting}>
            Create &amp; reveal venueKey
          </Button>
        </div>
      </div>
    </Modal>
  );
}
